import { type Category } from '@/services/api';
import { Link, useLocation } from "react-router-dom";
import { Settings, Archive, Star, Cable, Box, Gift } from "lucide-react";
import { getCategoryIcon, getPlatformIcon, getPlatforms } from "@/lib/utils";
import { trackSettingsViewed } from "@/utils/eventTracking";
import { NewSidebarBadge } from "@/components/NewSidebarBadge";

interface CategorySelection {
  id: string;
  category_enum: string;
  category_display_value: string;
  is_folder: boolean;
}

interface SidebarProps {
  isMenuOpen: boolean
  selectedCategory: CategorySelection | null;
  setSelectedCategory: (category: CategorySelection | null) => void;
  selectedPlatform: string;
  setSelectedPlatform: (platform: string) => void;
  className?: string;
  disabled?: boolean;
  viewingArchived?: boolean;
  onViewArchived?: () => void;
  viewingFavorites?: boolean;
  onViewFavorites?: () => void;
  categories: Category[];
  favoriteCount?: number;
  archivedCount?: number;
  platformCounts?: Array<{ platform: string; count: number; }>;
  totalPostsCount?: number;
}

export const Sidebar = ({
  isMenuOpen,
  selectedCategory,
  setSelectedCategory,
  selectedPlatform,
  setSelectedPlatform,
  className = '',
  disabled = false,
  viewingArchived = false,
  onViewArchived,
  viewingFavorites = false,
  onViewFavorites,
  categories,
  favoriteCount = 0,
  archivedCount = 0,
  platformCounts = [],
  totalPostsCount = 0
}: SidebarProps) => {
  const platforms = getPlatforms();
  const location = useLocation();

  // Helper function to get count for a platform
  const getPlatformCount = (platformId: string): number => {
    const platform = platforms.find(p => p.id === platformId);
    if (platform && platform.api_value) {
      const countObj = Array.isArray(platformCounts)
        ? platformCounts.filter((p: { platform: string; count: number; }) => p.platform === platform.api_value)[0]
        : undefined;
      return countObj?.count || 0;
    }
    return 0;
  };

  // Categories are now passed as props from Home component

  return (
    <div className={`w-64 bg-slate-900/30 p-6 max-h-[calc(100vh-180px)] md:max-h-none md:h-full overflow-y-auto border-r border-gray-700 ${className}`}>
      {/* Platform Section */}
      <div className={`mb-8 ${isMenuOpen ? "mt-10" : ""}`}>
        <h3 className={`text-sm font-semibold text-gray-400 mb-4 tracking-wider`}>Platform</h3>
        <div className="space-y-2">
          {platforms.map((platform) => {
            const platformCount = getPlatformCount(platform.id);
            const isSelected = selectedPlatform === platform.id && !viewingArchived && !viewingFavorites;
            return (
              <div
                key={platform.id}
                className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center justify-between ${
                  isSelected
                    ? "bg-purple-500/20 text-white" 
                    : "text-gray-300 hover:bg-gray-700 hover:text-white"
                } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => {
                  if (!disabled) {
                    setSelectedPlatform(platform.id);
                  }
                }}
              >
                <div className="flex items-center space-x-3">
                  {(() => {
                    const IconComponent = getPlatformIcon(platform.id);
                    return <IconComponent className="w-4 h-4" />;
                  })()}
                  <span className="text-sm font-medium">{platform.display_name}</span>
                </div>
                {platform.id === 'all_platforms' ? null : <span className={`text-xs px-2 py-1 rounded-full ${
                  isSelected
                    ? "bg-purple-200 text-purple-700"
                    : "bg-transparent text-gray-300"
                }`}>
                  {platformCount}
                </span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Categories Section */}
      <div className="mb-8">
        <h3 className="text-sm font-semibold text-gray-400 mb-4 tracking-wider">Categories</h3>
        <div className="space-y-2">
          
          {
            categories.map((category) => {
                const isSelected = selectedCategory && 
                  String(selectedCategory.id) === String(category.id) && 
                  selectedCategory.category_display_value === category.category_display_value && 
                  selectedCategory.is_folder === category.is_folder;
                
                
                return (
                  <div
                    key={`${category.id}-${category.category_display_value}-${category.is_folder}`}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center justify-between ${
                      isSelected && !viewingArchived && !viewingFavorites
                        ? "bg-purple-500/20 text-white"
                        : "text-gray-300 hover:bg-gray-700 hover:text-white"
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={() => {
                      if (!disabled) {
                        const categorySelection = {
                          id: category.id,
                          category_enum: category.category_enum,
                          category_display_value: category.category_display_value,
                          is_folder: category.is_folder
                        };
                        setSelectedCategory(categorySelection);
                      }
                    }}
                  >
                    <div className="flex items-center space-x-3">
                      {(() => {
                        const is_label = category.category_enum === "others" && category.category_display_value.toLowerCase() != "others"
                        const IconComponent = getCategoryIcon(category.category_enum, category.is_folder,is_label);
                        return <IconComponent className="w-4 h-4" />;
                      })()}
                      <span className="text-sm font-medium">
                        {category.category_display_value}
                      </span>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      isSelected && !viewingArchived && !viewingFavorites
                        ? "bg-purple-200 text-purple-700"
                        : "bg-transparent text-gray-300"
                    }`}>
                      {category.post_count}
                    </span>
                  </div>
                );
              })
          }
        </div>
      </div>

      {/* Quick Actions Section */}
      <div>
        <h3 className="text-sm font-semibold text-gray-400 mb-4 tracking-wider">Quick Actions</h3>
        <div className="space-y-2">
          <div
            className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center justify-between cursor-pointer ${
              viewingFavorites
                ? "bg-purple-500/20 text-white"
                : "text-gray-300 hover:bg-gray-700 hover:text-white"
            }`}
            onClick={() => {
              onViewFavorites?.();
            }}
          >
            <div className="flex items-center space-x-3">
              <Star className="w-4 h-4" />
              <span className="text-sm font-medium">Favorites</span>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full ${
              viewingFavorites
                ? "bg-purple-200 text-purple-700"
                : "bg-transparent text-gray-300"
            }`}>
              {favoriteCount}
            </span>
          </div>
          <div
            className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center justify-between cursor-pointer ${
              viewingArchived
                ? "bg-purple-500/20 text-white"
                : "text-gray-300 hover:bg-gray-700 hover:text-white"
            }`}
            onClick={() => {
              onViewArchived?.();
            }}
          >
            <div className="flex items-center space-x-3">
              <Archive className="w-4 h-4" />
              <span className="text-sm font-medium">Archives</span>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full ${
              viewingArchived
                ? "bg-purple-200 text-purple-700"
                : "bg-transparent text-gray-300"
            }`}>
              {archivedCount}
            </span>
          </div>
          {totalPostsCount > 0 && (
            <Link
              to="/3d-visualisation"
              className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center space-x-3 ${
                location.pathname === '/3d-visualisation'
                  ? "bg-purple-500/20 text-white"
                  : "text-gray-300 hover:bg-gray-700 hover:text-white"
              }`}
            >
              <Box className="w-4 h-4" />
              <span className="text-sm font-medium">Visualize me</span>
            </Link>
          )}
          <Link
            to="/profile?active=integrations"
            className="w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center space-x-3 text-gray-300 hover:bg-gray-700 hover:text-white"
          >
            <Cable className="w-4 h-4" />
            <span className="text-sm font-medium">Integrations</span>
          </Link>
          <Link
            to="/profile?active=profile"
            className="w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center space-x-3 text-gray-300 hover:bg-gray-700 hover:text-white"
            onClick={() => {
              trackSettingsViewed();
            }}
          >
            <Settings className="w-4 h-4" />
            <span className="text-sm font-medium">Settings</span>
          </Link>
        </div>
      </div>
    </div>
  );
};
